import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

const STATUS_LABELS = {
  PENDING_PAYMENT: 'Wacht op betaling', confirmed: 'Bevestigd', processing: 'In verwerking',
  shipped: 'Verzonden', delivered: 'Geleverd', cancelled: 'Geannuleerd', refunded: 'Terugbetaald',
};
const STATUS_COLORS = {
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-700',
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.listOrders(filter).then(r => {
      if (!cancelled) setOrders(r.data);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter]);

  const statuses = ['', 'PENDING_PAYMENT', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  return (
    <div data-testid="admin-orders-page" className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground">Bestellingen</h1>
          <p className="text-sm text-muted-foreground mt-1">{orders.length} bestellingen</p>
        </div>
        <Link
          to="/admin/orders/new"
          data-testid="new-manual-order-btn"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-sm"
        >
          + Nieuwe bestelling
        </Link>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {statuses.map(s => (
          <button key={s} data-testid={`filter-${s || 'all'}`} onClick={() => { setFilter(s); setLoading(true); }}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${filter === s ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border text-muted-foreground hover:border-primary/30'}`}>
            {s ? STATUS_LABELS[s] || s : 'Alle'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={`admin-orders-skeleton-${i}`} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3.5 text-left font-semibold">Bestelnr.</th>
                <th className="px-5 py-3.5 text-left font-semibold">Klant</th>
                <th className="px-5 py-3.5 text-right font-semibold">Totaal</th>
                <th className="px-5 py-3.5 text-center font-semibold">Status</th>
                <th className="px-5 py-3.5 text-left font-semibold">Datum</th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.map(o => (
                <tr key={o._id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-5 py-3.5 font-mono font-medium text-foreground">#{o.orderNumber}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{o.user?.name || o.user?.email || o.guestEmail || '--'}</td>
                  <td className="px-5 py-3.5 text-right font-semibold">{formatCurrency(o.totalAmount, 'nl')}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[o.status] || 'bg-muted text-muted-foreground'}`}>
                      {STATUS_LABELS[o.status] || o.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString('nl-BE', { dateStyle: 'medium' }) : '--'}</td>
                  <td className="px-5 py-3.5 text-right">
                    <Link to={`/admin/orders/${o._id}`} data-testid={`order-detail-${o._id}`} className="text-xs text-primary font-medium hover:underline">Detail</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!orders.length && <p className="text-center py-12 text-muted-foreground text-sm">Geen bestellingen gevonden</p>}
        </div>
      )}
    </div>
  );
}
